<template><section>
<h1>Icon 图标</h1>
<p>提供了一套常用的图标集合。</p>
<h2>基本用法</h2>
<p>:::demo</p>
<pre v-pre=""><code v-pre="" class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">lang</span>=<span class="hljs-string">"pug"</span>&gt;</span>
.icons 
  se-icon(name="time-14-14-1")
  se-icon(name="class")
  se-icon(name="pic")
  se-icon(name="hover-date-datepicke")
  se-icon(name="pic-upload1")
  se-icon(name="loading-32-32-21")
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
</code></pre>
<p>:::</p>
<h2>图标大小</h2>
<p>:::demo</p>
<pre v-pre=""><code v-pre="" class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">lang</span>=<span class="hljs-string">"pug"</span>&gt;</span>
se-icon(name="time-14-14-1" size="14px")
se-icon(name="time-14-14-1" size="20px")
se-icon(name="time-14-14-1" size="28px")
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
</code></pre>
<p>:::</p>
<h2>图标颜色</h2>
<p>:::demo</p>
<pre v-pre=""><code v-pre="" class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">lang</span>=<span class="hljs-string">"pug"</span>&gt;</span>
.icons
  se-icon(name="time-14-14-1" style="color: #4bb311")
  se-icon(name="time-14-14-1" style="color: #dc4848")
  se-icon(name="time-14-14-1" style="color: #e6a600")
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
</code></pre>
<p>:::</p>
<h2>图标集合</h2>
<p>:::demo</p>
<pre v-pre=""><code v-pre="" class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span> <span class="hljs-attr">lang</span>=<span class="hljs-string">"pug"</span>&gt;</span>
.icons
  .item(
    v-for="(svg, index) in svgs"
    @mouseover="sizes[svg.id] = 'xl'"
    @mouseleave="sizes[svg.id] = 'lg'"
    :data-clipboard-text="svg.id"
  )
    .icon
      se-icon(
        :name="getIconName(svg.id)"
        :size="sizes[svg.id]"
      )
    .name {{ getIconName(svg.id) }}
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>

<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="javascript">
  <span class="hljs-keyword">import</span> toast <span class="hljs-keyword">from</span> <span class="hljs-string">'../../helpers/toast'</span>
  <span class="hljs-keyword">import</span> Clipboard <span class="hljs-keyword">from</span> <span class="hljs-string">'clipboard'</span>

  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    data() {
      <span class="hljs-keyword">const</span> svgs = <span class="hljs-built_in">document</span>.querySelector(<span class="hljs-string">'svg'</span>).children
      <span class="hljs-keyword">const</span> sizes = [...svgs].reduce(<span class="hljs-function">(<span class="hljs-params">obj, svg</span>) =&gt;</span> {
        obj[svg.id] = <span class="hljs-string">'lg'</span>
        <span class="hljs-keyword">return</span> obj
      }, {})

      <span class="hljs-keyword">return</span> {
        svgs,
        sizes,
      }
    },

    created() {
      <span class="hljs-keyword">const</span> clipboard = <span class="hljs-keyword">new</span> Clipboard(<span class="hljs-string">'.item'</span>)

      clipboard.on(<span class="hljs-string">'success'</span>, <span class="hljs-function"><span class="hljs-keyword">function</span>(<span class="hljs-params">e</span>) </span>{
        toast.success(<span class="hljs-string">`<span class="hljs-subst">${e.text}</span> copied!`</span>)
      })
    },

    <span class="hljs-attr">methods</span>: {
      getIconName(str) {
        <span class="hljs-keyword">return</span> str.replace(<span class="hljs-string">'se-icon-'</span>, <span class="hljs-string">''</span>)
      },
    },
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
</code></pre>
<p>:::</p>
<h2>Props</h2>
<table>
<thead>
<tr>
<th>属性</th>
<th>说明</th>
<th>类型</th>
<th>可选值</th>
<th>默认值</th>
</tr>
</thead>
<tbody>
<tr>
<td>name</td>
<td>图标名称</td>
<td>string</td>
<td>-</td>
<td>-</td>
</tr>
<tr>
<td>size</td>
<td>尺寸</td>
<td>string</td>
<td>lg\xl\md 或者自定大小</td>
<td>md</td>
</tr>
</tbody>
</table>
<h2>Notes</h2>
<p>Loading 主题的图标固定绑定了旋转动画，因为 Loading 图标只有旋转的时候才成立。
如果有 Loading 图标的名称发生了变化，请在 icon.sass 进行相对应的修改，否则其图标将会失去旋转动画。</p>
</section></template>
<style lang="sass" scoped>
  .icons .se-icon
    margin: 0 6px
  .icons
    display: flex
    flex-wrap: wrap
    flex-direction: row
    justify-content: flex-start
    .item
      width: 20%
      height: 100px
      text-align: center
      border-radius: 4px
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      .icon
        display: flex
        align-items: center
        justify-content: center
        height: 70px
      .name
        margin-top: -10px
        width: 80%
        height: 30px
        overflow: hidden
        white-space: nowrap
        text-overflow: ellipsis
      &:hover
        color: #fff
        background-color: #3388f6
        cursor: pointer
</style>
<script>
  import toast from '../../helpers/toast'
  import Clipboard from 'clipboard'

  export default {
    data() {
      const svgs = document.querySelector('svg').children
      const sizes = [...svgs].reduce((obj, svg) => {
        obj[svg.id] = 'lg'
        return obj
      }, {})

      return {
        svgs,
        sizes,
      }
    },

    created() {
      const clipboard = new Clipboard('.item')

      clipboard.on('success', function(e) {
        toast.success(`${e.text} copied!`)
      })
    },

    methods: {
      getIconName(str) {
        return str.replace('se-icon-', '')
      },
    },
  }
</script>